import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Printer, WifiOff, Wifi } from 'lucide-react';
import { clientsApi } from '../../api/endpoints/clients';
import { Card, StatCard } from '../../components/ui/Card';
import { StatusBadge } from '../../components/ui/Badge';
import { Table, Thead, Th, Td, Tr } from '../../components/ui/Table';
import { MetricBar } from '../../components/ui/MetricBar';
import { PageSpinner } from '../../components/ui/Spinner';
import { formatDate, formatDuration, formatPercent, formatTimeAgo } from '../../utils/formatters';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

export function ClientReportPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const [timelineHours, setTimelineHours] = useState(24);

  const metricsRange = timelineHours >= 720 ? '30d'
    : timelineHours >= 168 ? '7d'
    : timelineHours >= 48 ? '48h'
    : timelineHours >= 24 ? '24h'
    : '6h';

  const { data: clientData, isLoading } = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => clientsApi.get(clientId!),
    enabled: !!clientId,
  });

  const { data: timeline } = useQuery({
    queryKey: ['timeline', clientId, timelineHours],
    queryFn: () => clientsApi.timeline(clientId!, timelineHours),
    enabled: !!clientId,
  });

  const { data: metrics } = useQuery({
    queryKey: ['metrics', clientId, metricsRange],
    queryFn: () => clientsApi.metrics(clientId!, metricsRange),
    enabled: !!clientId,
  });

  const { data: offlineData } = useQuery({
    queryKey: ['offline', clientId, 1, 50],
    queryFn: () => clientsApi.offlineEvents(clientId!, 1, 50),
    enabled: !!clientId,
  });

  if (isLoading) return <PageSpinner />;

  const client = clientData?.client;
  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Client not found</p>
        <Link to="/clients" className="text-purple-600 text-sm mt-2 inline-block">Back to clients</Link>
      </div>
    );
  }

  const analytics = timeline?.analytics;
  const metricsHistory = metrics?.metrics || [];
  const offlineEvents = offlineData?.events || [];
  const generatedAt = new Date().toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });

  const rangeLabel = timelineHours >= 720 ? 'Last 30 Days'
    : timelineHours >= 168 ? 'Last 7 Days'
    : timelineHours >= 48 ? 'Last 48 Hours'
    : timelineHours >= 24 ? 'Last 24 Hours'
    : 'Last 6 Hours';

  return (
    <>
      {/* Print styles */}
      <style>{`
        @media print {
          nav, .admin-layout-nav, .no-print { display: none !important; }
          main { padding: 0 !important; max-width: 100% !important; }
          body { background: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print-break { page-break-before: always; }
          .dark .print-force-light { background: white !important; color: #1f2937 !important; border-color: #e5e7eb !important; }
          .dark .print-force-light * { color: #1f2937 !important; }
        }
      `}</style>

      {/* Controls bar -- hidden when printing */}
      <div className="no-print flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Link to={`/clients/${client.client_id}`} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">Client Report</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">{client.deployment_name || client.client_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={timelineHours}
            onChange={e => setTimelineHours(Number(e.target.value))}
            className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 dark:bg-gray-700 dark:text-white"
          >
            <option value={6}>Last 6 Hours</option>
            <option value={24}>Last 24 Hours</option>
            <option value={48}>Last 48 Hours</option>
            <option value={168}>Last 7 Days</option>
            <option value={720}>Last 30 Days</option>
          </select>
          <button
            onClick={() => window.print()}
            className="px-3 py-1.5 bg-purple-600 text-white rounded-md text-xs hover:bg-purple-700 flex items-center gap-1"
          >
            <Printer size={14} />
            Print / PDF
          </button>
        </div>
      </div>

      {/* Report content */}
      <div className="print-force-light">
        {/* Report header */}
        <div className="bg-gray-900 dark:bg-gray-900 rounded-lg p-6 mb-4 text-center print-force-light" style={{ printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }}>
          <img
            src="https://www.restroworks.com/wp-content/uploads/2025/04/Restroworks-White.png"
            alt="Restroworks"
            className="h-[70px] mx-auto mb-3"
          />
          <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-widest">Monitoring Report</h2>
          <p className="text-base font-semibold text-gray-200 mt-1">{client.deployment_name || client.client_name}</p>
          <p className="text-[11px] text-gray-400 mt-1">Generated on {generatedAt} &bull; {rangeLabel}</p>
        </div>

        {/* Section 1: Client Information */}
        <Card title="Client Information" className="mb-4">
          <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
            {([
              ['Name', client.deployment_name || client.client_name || '-'],
              ['Client ID', client.client_id],
              ['Status', null],
              ['Hostname', client.hostname || '-'],
              ['Local IP', client.local_ip || '-'],
              ['Public IP', client.public_ip || '-'],
              ['SSH Port', client.port || '-'],
              ['HTTP Port', client.api_port || '-'],
              ['Integration', client.integration_id || '-'],
              ['Deployment', client.deployment_name || '-'],
              ['Tenant ID', client.tenant_id || '-'],
              ['Last Seen', formatTimeAgo(client.last_seen)],
              ['Tags', (client.tags && client.tags.length > 0) ? client.tags.join(', ') : '-'],
              ['Created', formatDate(client.createdAt)],
              ['Uptime', client.metrics?.uptime_seconds ? formatDuration(client.metrics.uptime_seconds * 1000) : '-'],
            ] as [string, string | number | null][]).map(([label, value]) => (
              <div key={label} className="bg-gray-50 dark:bg-gray-900/50 rounded-md px-3 py-2">
                <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase font-semibold tracking-wide">{label}</p>
                {label === 'Status' ? (
                  <div className="mt-0.5"><StatusBadge status={client.status} /></div>
                ) : (
                  <p className="text-xs font-medium text-gray-800 dark:text-gray-200 mt-0.5 break-all">{String(value)}</p>
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* Section 2: Uptime Summary */}
        {analytics && (
          <Card title="Uptime Summary" className="mb-4">
            <div className="p-4">
              <div className="grid grid-cols-5 gap-3 mb-4">
                <StatCard label="Uptime" value={`${analytics.uptime_percent.toFixed(1)}%`} color="green" />
                <StatCard label="Downtime" value={`${(100 - analytics.uptime_percent).toFixed(1)}%`} color="red" />
                <StatCard label="Disconnections" value={analytics.disconnect_count} color="yellow" />
                <StatCard label="Avg Down" value={formatDuration(analytics.avg_downtime_ms)} color="gray" />
                <StatCard label="Longest Down" value={formatDuration(analytics.longest_downtime_ms)} color="gray" />
              </div>

              {/* Timeline bar */}
              <ReportTimelineBar changes={timeline?.changes || []} period={timeline?.period} />
            </div>
          </Card>
        )}

        {/* Section 3: Current Health / Resource Usage */}
        <Card title="Current Health Snapshot" className="mb-4">
          <div className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <HealthItem label="Tunnel Status" value={client.metrics?.tunnel_status || '-'} status={client.metrics?.tunnel_status === 'active' ? 'good' : 'bad'} />
              <HealthItem label="SSH Connection" value={client.metrics?.ssh_connection || '-'} status={client.metrics?.ssh_connection === 'active' ? 'good' : 'bad'} />
              <HealthItem
                label="Latency"
                value={client.metrics?.latency_ms != null ? `${client.metrics.latency_ms}ms` : '-'}
                status={client.metrics?.latency_ms != null ? (client.metrics.latency_ms < 100 ? 'good' : client.metrics.latency_ms < 300 ? 'warn' : 'bad') : 'neutral'}
              />
              <HealthItem
                label="Reconnects"
                value={client.metrics?.reconnect_count != null ? String(client.metrics.reconnect_count) : '-'}
                status={client.metrics?.reconnect_count != null ? (client.metrics.reconnect_count > 5 ? 'warn' : 'good') : 'neutral'}
              />
            </div>
            <div className="space-y-2">
              <MetricBar value={client.metrics?.cpu_percent} label="CPU" size="md" color="blue" />
              <MetricBar value={client.metrics?.memory_percent} label="MEM" size="md" color="indigo" />
              <MetricBar value={client.metrics?.disk_percent} label="DISK" size="md" color="purple" />
            </div>
          </div>
        </Card>

        {/* Section 4: Resource Usage Chart */}
        <Card title="Resource Usage Over Time" className="mb-4">
          <div className="p-4 h-64">
            {metricsHistory.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={metricsHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="timestamp"
                    tickFormatter={t => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 11 }} labelFormatter={t => new Date(t).toLocaleString()} />
                  <Line type="monotone" dataKey="cpu_percent" stroke="#ef4444" name="CPU" dot={false} strokeWidth={1.5} />
                  <Line type="monotone" dataKey="memory_percent" stroke="#eab308" name="Memory" dot={false} strokeWidth={1.5} />
                  <Line type="monotone" dataKey="disk_percent" stroke="#3b82f6" name="Disk" dot={false} strokeWidth={1.5} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-gray-400">No metrics data available for this period</div>
            )}
          </div>
          {metricsHistory.length > 0 && (
            <p className="text-center text-[10px] text-gray-400 pb-3">{metricsHistory.length} data points</p>
          )}
        </Card>

        {/* Section 5: Offline Events */}
        <Card title={`Offline Events (${offlineEvents.length})`} className="mb-4 print-break">
          {offlineEvents.length > 0 ? (
            <Table>
              <Thead>
                <tr>
                  <Th>#</Th>
                  <Th>Went Offline</Th>
                  <Th>Came Online</Th>
                  <Th>Duration</Th>
                </tr>
              </Thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {offlineEvents.map((evt, i) => (
                  <Tr key={i}>
                    <Td><span className="text-gray-400">{i + 1}</span></Td>
                    <Td>
                      <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
                        <WifiOff size={12} /> {formatDate(evt.went_offline)}
                      </span>
                    </Td>
                    <Td>
                      {evt.came_online ? (
                        <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                          <Wifi size={12} /> {formatDate(evt.came_online)}
                        </span>
                      ) : (
                        <span className="text-red-500 text-xs font-semibold">Still offline</span>
                      )}
                    </Td>
                    <Td>
                      <span className={getDurationClass(evt.duration_ms)}>
                        {formatDuration(evt.duration_ms)}
                      </span>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="p-6 text-center text-sm text-gray-400">No offline events in this period</div>
          )}
        </Card>

        {/* Footer */}
        <div className="mt-6 mb-4 p-3 bg-gray-50 dark:bg-[#111] border border-gray-200 dark:border-gray-800 rounded-lg text-center">
          <p className="text-[10px] text-gray-400">
            Generated by Restoworks Connect &bull; {generatedAt}
          </p>
        </div>
      </div>
    </>
  );
}

// Duration severity class
function getDurationClass(ms: number): string {
  if (ms < 300000) return 'text-green-600 dark:text-green-400';
  if (ms < 1800000) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400 font-semibold';
}

// Health indicator item
function HealthItem({ label, value, status }: { label: string; value: string; status: 'good' | 'warn' | 'bad' | 'neutral' }) {
  const statusColor = {
    good: 'text-green-600 dark:text-green-400',
    warn: 'text-yellow-600 dark:text-yellow-400',
    bad: 'text-red-600 dark:text-red-400',
    neutral: 'text-gray-600 dark:text-gray-400',
  };

  return (
    <div className="bg-white dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 flex items-center justify-between">
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
      <span className={`text-sm font-bold ${statusColor[status]}`}>{value}</span>
    </div>
  );
}

// Timeline bar for report (same logic as ClientDetailPage)
function ReportTimelineBar({ changes, period }: { changes: Array<{ status: string; timestamp: string }>; period?: { since: string; until: string } }) {
  if (!period) return null;
  const start = new Date(period.since).getTime();
  const end = new Date(period.until).getTime();
  const total = end - start;
  if (total <= 0) return null;

  const segments: Array<{ left: number; width: number; online: boolean }> = [];
  let lastTime = start;
  let lastOnline = true;

  for (const change of changes) {
    const t = new Date(change.timestamp).getTime();
    if (t > lastTime && t <= end) {
      segments.push({
        left: ((lastTime - start) / total) * 100,
        width: ((t - lastTime) / total) * 100,
        online: lastOnline,
      });
      lastTime = t;
    }
    lastOnline = change.status === 'connected';
  }
  segments.push({
    left: ((lastTime - start) / total) * 100,
    width: ((end - lastTime) / total) * 100,
    online: lastOnline,
  });

  return (
    <div>
      <div className="relative h-6 rounded-md overflow-hidden bg-gray-200 dark:bg-gray-700">
        {segments.map((seg, i) => (
          <div
            key={i}
            className={seg.online ? 'bg-emerald-500' : 'bg-rose-600'}
            style={{ position: 'absolute', left: `${seg.left}%`, width: `${seg.width}%`, height: '100%' }}
          />
        ))}
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-[10px] text-gray-400">{new Date(period.since).toLocaleString()}</span>
        <span className="text-[10px] text-gray-400">{new Date(period.until).toLocaleString()}</span>
      </div>
    </div>
  );
}
